import { AppValue, expandAppView } from 'lib/actions/apps'
import { AppUserValue, expandAppUserView } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user'
import { expandValue } from 'lib/rpcts'
import { App, AppAndOwners, AppRef, MultiGetAppRequest, MultiGetAppResponse } from 'pbts/c1/api/app/v1/app'
import { AppsClient, AppSearchClient } from 'pbts/c1/api/app/v1/app.client'
import { AppAccessRequestDefaultsRef } from 'pbts/c1/api/app/v1/app_access_requests_defaults'
import { AppAccessRequestsDefaultsServiceClient } from 'pbts/c1/api/app/v1/app_access_requests_defaults.client'
import { AppAttributeSearchServiceClient } from 'pbts/c1/api/app/v1/app_attributes.client'
import {
    AppManagedStateBinding,
    AppManagedStateBindingRef,
    AppManagedStateBindingView,
    AppManagedStateSearchTermsResponse,
    GetAppManagedStateBindingResponse,
} from 'pbts/c1/api/app/v1/app_managed'
import { AppManagedStateSearchServiceClient, AppManagedStateServiceClient } from 'pbts/c1/api/app/v1/app_managed.client'
import { AppResource, AppResourceType, AppResourceTypeRef } from 'pbts/c1/api/app/v1/app_resource'
import { AppResourceSearchClient } from 'pbts/c1/api/app/v1/app_resource.client'
import { AppUser, AppUserRef, AppUserView } from 'pbts/c1/api/app/v1/app_user'
import { AppUserServiceClient } from 'pbts/c1/api/app/v1/app_user.client'
import { InventoryAlert, InventoryAlertView, HistoricalInventoryAlert, HistoricalInventoryAlertView } from 'pbts/c1/api/inventory/v1/inventory_alerts'
import { InventoryAlertSearchClient } from 'pbts/c1/api/inventory/v1/inventory_alerts.client'
import { FacetValue } from 'pbts/c1/api/search/v1/facet'
import { Any } from 'pbts/google/protobuf/any'
import { createMultiGetApi, MultiGetOpts } from 'redux_toolkit/lib/createMultiGetApi'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from 'redux_toolkit/lib/createUnaryApi'

const transformUserValueArrayResponse = (resp): Array<AppValue> => {
    return resp.list.map((v): AppValue => expandAppView(v, resp.expanded))
}

export type AppAndOwnerValues = {
    App: AppValue
    Owners: Array<UserValue>
}

export type AppUserViewExpanded = AppUserView & {
    id: string
    appId: string
    app?: App
}

export type AppManagedStateBindingValue = AppManagedStateBinding & {
    app?: App
    id: string
}

export type InventoryAlertValue = {
    inventoryAlert?: InventoryAlert
    resource?: AppResource
    appResourceType?: AppResourceType
    app?: App
    appUser?: AppUser
}

export type HistoricalInventoryAlertValue = {
    inventoryAlert?: HistoricalInventoryAlert
    resource?: AppResource
    appResourceType?: AppResourceType
    app?: App
    appUser?: AppUser
}

const transformAppUserViewArrayResponse = (resp): Array<AppUserViewExpanded> => {
    return resp.list.map((v: AppUserView): AppUserViewExpanded => {
        return {
            ...v,
            id: v.appUser?.id || v.appUser?.identityUserId || '',
            appId: v.appUser?.appId || '',
            app: expandValue(v.appPath, App, resp.expanded),
        }
    })
}

const transformAppUserValueArrayResponse = (resp): Array<AppUserValue> => {
    return resp.list.map((v: AppUserView): AppUserValue => {
        return expandAppUserView(v, resp.expanded)
    })
}

const transformAppAndOwnerValueArrayResponse = (resp): Array<AppAndOwnerValues> => {
    return resp.list.map((v: AppAndOwners): AppAndOwnerValues => {
        const app = expandAppView(v.appView, resp.expanded)
        return { App: app, Owners: v.appOwners }
    })
}

const transformAppManagedStateBinding = (v: AppManagedStateBindingView | undefined, expanded: Any[]): AppManagedStateBindingValue => ({
    ...AppManagedStateBinding.create({ ...v?.appManagementStateBinding }),
    app: expandValue(v?.appPath, App, expanded),
    id: v?.appManagementStateBinding?.resourceId || '',
})

const transformAppManagedStateBindingListResponse = (resp): Array<AppManagedStateBindingValue> => {
    return resp.list.map((v): AppManagedStateBindingValue => transformAppManagedStateBinding(v, resp.expanded))
}

const transformAppManagedStateBindingGetResponse = (resp: GetAppManagedStateBindingResponse): AppManagedStateBindingValue => {
    return transformAppManagedStateBinding(resp.appManagementState, resp.expanded)
}

const transformInventoryAlertView = (v: InventoryAlertView, expanded: Any[]): InventoryAlertValue => {
    if (!expanded) {
        return {
            inventoryAlert: v.inventoryAlert,
        }
    }
    return {
        inventoryAlert: v.inventoryAlert,
        resource: expandValue(v.resourcePath, AppResource, expanded),
        appResourceType: expandValue(v.appResourceTypePath, AppResourceType, expanded),
        app: expandValue(v.appPath, App, expanded),
        appUser: expandValue(v.appUserPath, AppUser, expanded),
    }
}

const transformHistoricalInventoryAlertView = (v: HistoricalInventoryAlertView, expanded: Any[]): HistoricalInventoryAlertValue => {
    if (!expanded) {
        return {
            inventoryAlert: v.historicalInventoryAlert,
        }
    }
    return {
        inventoryAlert: v.historicalInventoryAlert,
        resource: expandValue(v.resourcePath, AppResource, expanded),
        appResourceType: expandValue(v.appResourceTypePath, AppResourceType, expanded),
        app: expandValue(v.appPath, App, expanded),
        appUser: expandValue(v.appUserPath, AppUser, expanded),
    }
}

const transformHistoricalInventoryAlertViewArrayResponse = (resp): Array<HistoricalInventoryAlertValue> => resp.list
    .map((v): HistoricalInventoryAlertValue => transformHistoricalInventoryAlertView(v, resp.expanded))

const transformInventoryAlertViewArrayResponse = (resp): Array<InventoryAlertValue> => resp.list
    .map((v): InventoryAlertValue => transformInventoryAlertView(v, resp.expanded))

export const appSearchApi = createPagedListApi(
    AppSearchClient,
    'searchAppsExpanded',
    transformUserValueArrayResponse,
    AppRef.typeName,
)

export const appSearchNonExpandedApi = createUnaryApi(AppSearchClient, 'search')

export const appGetApi = createUnaryApi(
    AppsClient,
    'get',
    (resp): App => (resp.app ? resp.app : App.create()),
    AppRef.typeName,
)

const multiGetAppOpts: MultiGetOpts<AppRef, MultiGetAppRequest, MultiGetAppResponse, AppValue> = {
    buildRequest: (refs: AppRef[]) => MultiGetAppRequest.create({
        appIds: refs.map((ref) => ref.id),
        expandMask: { paths: ['*'] },
    }),
    transformResponse: (res: MultiGetAppResponse) => {
        return res.list.map((view): AppValue => expandAppView(view, res.expanded))
    },
    refExtractor: (item: AppValue) => {
        return { id: item.id }
    },
    maxBatchSize: 32,
    tag: AppRef.typeName,
}

export const appMultiGetApi = createMultiGetApi(
    AppsClient,
    'multiGet',
    multiGetAppOpts,
)

export const appUpdateApi = createMutationApi(AppsClient, 'update')

export const appUserApi = createPagedListApi(
    AppUserServiceClient,
    'search',
    transformAppUserViewArrayResponse,
)

export const countOrphanedAccounts = createUnaryApi(AppUserServiceClient, 'countOrphanedAccounts')
export const appResourceSearchTypeTraitsApi = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourceTypeTraitsRollup',
    undefined,
    AppResourceTypeRef.typeName,
)
export const totalCountOrphanedAccounts = createUnaryApi(AppUserServiceClient, 'totalCountOrphanedAccounts')

export const searchOrphanedAccounts = createPagedListApi(
    AppUserServiceClient,
    'searchOrphanedAccounts',
    transformAppUserValueArrayResponse,
    AppUserRef.typeName,
)

export const getInactiveOwnersCounts = createUnaryApi(AppSearchClient, 'getInactiveOwnersCounts')

export const searchAppsWithDeactivatedOwner = createPagedListApi(
    AppSearchClient,
    'searchAppsWithDeactivatedOwner',
    transformAppAndOwnerValueArrayResponse,
)

export const createAppRequestsDefaults = createMutationApi(AppAccessRequestsDefaultsServiceClient, 'createAppAccessRequestsDefaults')

export const getAppAccessRequestsDefaults = createUnaryApi(
    AppAccessRequestsDefaultsServiceClient,
    'getAppAccessRequestsDefaults',
    undefined,
    AppAccessRequestDefaultsRef.typeName,
)

export const cancelAppAccessRequestsDefaults = createUnaryApi(
    AppAccessRequestsDefaultsServiceClient,
    'cancelAppAccessRequestsDefaults',
)

export const deleteAppApi = createMutationApi(
    AppsClient,
    'delete',
    undefined,
    AppRef.typeName,
)

export const createAppApi = createMutationApi(
    AppsClient,
    'create',
    undefined,
    AppRef.typeName,
)

export const createAppWithConnectorApi = createMutationApi(
    AppsClient,
    'createWithConnector',
    undefined,
    AppRef.typeName,
)

export const createAppWithUnmanagedAppApi = createMutationApi(
    AppsClient,
    'createWithUnmanagedApp',
    undefined,
    AppRef.typeName,
)

export const upliftAppUsersApi = createMutationApi(
    AppsClient,
    'upliftAppUsers',
)

export const unmanagedAppsSearchApi = createPagedListApi(
    AppManagedStateSearchServiceClient,
    'search',
    transformAppManagedStateBindingListResponse,
    AppManagedStateBindingRef.typeName,
)

export const promoteUnmanagedAppApi = createMutationApi(
    AppManagedStateServiceClient,
    'promote',
    transformAppManagedStateBindingGetResponse,
    // AppManagedStateBindingRef.typeName,
)

export const inventoryAlertsSearchApi = createPagedListApi(
    InventoryAlertSearchClient,
    'searchInventoryAlerts',
    transformInventoryAlertViewArrayResponse,
)

export const historicalInventoryAlertsSearchApi = createPagedListApi(
    InventoryAlertSearchClient,
    'searchHistoricalInventoryAlerts',
    transformHistoricalInventoryAlertViewArrayResponse,
)

const transformFacetResponse = (d: AppManagedStateSearchTermsResponse): Array<FacetValue> => {
    const item = d?.facets?.facets?.[0]?.item
    return item?.oneofKind === 'value' ? item.value?.values : []
}

export const appManagedStateSearchTermsApi = createUnaryApi(
    AppManagedStateSearchServiceClient,
    'getSearchTerms',
    transformFacetResponse,
)

export const appAttributeSearchApi = createUnaryApi(
    AppAttributeSearchServiceClient,
    'search',
)
